import { useContext, useEffect, useState } from 'react';

import Message from '../../../components/ui/Message';
import FormModal from '../../../components/ui/FormModal';
import { LoyaltyContext } from '../../../context/loyalty/LoyaltyContext';
import { LoyaltyConfiguration } from '../../../models/interfaces';

// Marcas que acepta el API, con ese texto exacto; "Generica" aplica cuando no hay configuración para la marca de la tarjeta
const BRANDS = ['Mastercard', 'Visa', 'Amex', 'Generica'];
const STAMPS_META_MAX = 50;

const emptyConfiguration: LoyaltyConfiguration = {
    moneda: 'CRC',
    marca: 'Generica',
    tipoPrograma: 'puntos',
    montoPorPunto: 1000,
    sellosPorCompra: 1,
    sellosMeta: 10,
    activo: true,
};

const currencySymbol = (currency: string) => currency === 'USD' ? '$' : '₡';
const isStamps = (config: LoyaltyConfiguration) => String(config.tipoPrograma ?? '').toLowerCase() === 'sellos';
const isInteger = (value: string) => /^\d+$/.test(value.trim());

export const LoyaltyConfigurationsTab = () => {
    const { configurations, loadConfigurations, saveConfiguration } = useContext(LoyaltyContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [editing, setEditing] = useState<LoyaltyConfiguration | null>(null);
    const [isNew, setIsNew] = useState(false);
    const [form, setForm] = useState({ moneda: 'CRC', marca: 'Generica', tipoPrograma: 'puntos', montoPorPunto: '1000', sellosPorCompra: '1', sellosMeta: '10', activo: true });
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    const refresh = async () => {
        setIsLoading(true);
        const resp = await loadConfigurations();
        setIsLoading(false);
        if (resp !== 'success') setError(resp);
    };

    useEffect(() => {
        refresh();
    }, []);

    const openForm = (configuration: LoyaltyConfiguration | null) => {
        const base = configuration ?? emptyConfiguration;
        const stampsConfig = isStamps(base);
        setIsNew(!configuration);
        setEditing(base);
        setForm({
            moneda: base.moneda ?? 'CRC',
            marca: base.marca ?? 'Generica',
            tipoPrograma: stampsConfig ? 'sellos' : 'puntos',
            montoPorPunto: stampsConfig ? '1000' : String(base.montoPorPunto ?? ''),
            sellosPorCompra: String(base.sellosPorCompra || 1),
            sellosMeta: String(base.sellosMeta || 10),
            activo: base.activo !== false,
        });
        setSubmitted(false);
        setFormError('');
    };

    const stamps = form.tipoPrograma === 'sellos';
    const perPurchase = Number(form.sellosPorCompra);
    const meta = Number(form.sellosMeta);
    const formErrors = {
        marca: !form.marca.trim(),
        montoPorPunto: !stamps && !(Number(form.montoPorPunto) > 0),
        sellosPorCompra: stamps && (!isInteger(form.sellosPorCompra) || perPurchase < 1),
        sellosMeta: stamps && (!isInteger(form.sellosMeta) || meta < 1 || meta > STAMPS_META_MAX),
        sellosPorCompraMeta: stamps && perPurchase >= 1 && meta >= 1 && perPurchase > meta,
    };
    const invalid = (field: keyof typeof formErrors) => submitted && formErrors[field] ? 'is-invalid' : '';

    // Si la marca guardada no está en la lista, se muestra como una opción más para no perderla
    const brandOptions = BRANDS.some(b => b.toLowerCase() === form.marca.toLowerCase()) ? BRANDS : [...BRANDS, form.marca];

    // La llave es moneda + marca + programa: si ya existe, se actualiza esa configuración
    const sameKey = (c: LoyaltyConfiguration) =>
        c.moneda === form.moneda
        && String(c.marca ?? '').toLowerCase() === form.marca.trim().toLowerCase()
        && (isStamps(c) ? 'sellos' : 'puntos') === form.tipoPrograma;
    const duplicate = isNew && configurations.some(sameKey);

    const toPayload = (source: typeof form): LoyaltyConfiguration => {
        const stampsPayload = source.tipoPrograma === 'sellos';
        return {
            moneda: source.moneda,
            marca: source.marca.trim(),
            tipoPrograma: source.tipoPrograma,
            montoPorPunto: stampsPayload ? 0 : Number(source.montoPorPunto),
            sellosPorCompra: stampsPayload ? Number(source.sellosPorCompra) : 0,
            ...(stampsPayload ? { sellosMeta: Number(source.sellosMeta) } : {}),
            activo: source.activo,
        };
    };

    const updatedCardsMessage = (count?: number) =>
        count && count > 0 ? `\nSe actualizaron ${count} tarjetas de clientes.` : '';

    const handleSave = async () => {
        setSubmitted(true);
        setFormError('');
        if (Object.values(formErrors).some(Boolean)) return;

        setSaving(true);
        const resp = await saveConfiguration(toPayload(form));
        setSaving(false);

        if (resp.error) {
            setFormError(resp.error);
            return;
        }
        setEditing(null);
        setSuccess((isNew && !duplicate ? 'Configuración creada correctamente.' : 'Configuración actualizada correctamente.')
            + updatedCardsMessage(resp.clientesActualizados));
        refresh();
    };

    const toggleActive = async (configuration: LoyaltyConfiguration) => {
        const stampsConfig = isStamps(configuration);
        const resp = await saveConfiguration({
            ...configuration,
            tipoPrograma: stampsConfig ? 'sellos' : 'puntos',
            montoPorPunto: stampsConfig ? 0 : configuration.montoPorPunto,
            activo: !configuration.activo,
        });
        if (resp.error) setError(resp.error);
        else refresh();
    };

    return (
        <>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div className="text-muted small">
                    <i className="bi bi-info-circle me-1" />
                    Define cuántos puntos o sellos gana el cliente por compra. Si la marca de la tarjeta no tiene configuración, se usa la <span className="fw-semibold">Generica</span>.
                </div>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary" title="Actualizar" onClick={refresh} disabled={isLoading}>
                        <i className="bi bi-arrow-clockwise" />
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => openForm(null)}>
                        <i className="bi bi-plus-lg me-1" /> Nueva configuración
                    </button>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-hover align-middle">
                    <thead>
                        <tr className="text-muted small text-uppercase">
                            <th>Marca</th>
                            <th>Moneda</th>
                            <th>Programa</th>
                            <th>Regla de acumulación</th>
                            <th>Estado</th>
                            <th style={{ textAlign: 'center' }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={6} className="text-center py-4">
                                <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                            </td></tr>
                        ) : configurations.length > 0 ? (
                            configurations.map(config => (
                                <tr key={config.id ?? `${config.moneda}-${config.marca}-${config.tipoPrograma}`} className={config.activo ? '' : 'text-muted'}>
                                    <td className="fw-medium">
                                        {config.marca}
                                        {String(config.marca).toLowerCase() === 'generica' && <span className="badge bg-light text-muted border ms-2">Por defecto</span>}
                                    </td>
                                    <td>{config.moneda}</td>
                                    <td>
                                        <span className={`badge rounded-pill ${isStamps(config) ? 'bg-info-subtle text-info-emphasis' : 'bg-primary-subtle text-primary-emphasis'}`}>
                                            {isStamps(config) ? 'Sellos' : 'Puntos'}
                                        </span>
                                    </td>
                                    <td className="small">
                                        {isStamps(config)
                                            ? <>{config.sellosPorCompra || 1} {(config.sellosPorCompra || 1) === 1 ? 'sello' : 'sellos'} por compra{config.sellosMeta ? <> · tarjeta de {config.sellosMeta}</> : null}</>
                                            : <>1 punto por cada {currencySymbol(config.moneda)}{Number(config.montoPorPunto).toLocaleString('en-US')}</>}
                                    </td>
                                    <td>
                                        <span className={`badge rounded-pill ${config.activo ? 'bg-success-subtle text-success-emphasis' : 'bg-secondary-subtle text-secondary-emphasis'}`}>
                                            {config.activo ? 'Activa' : 'Inactiva'}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'center', width: 110, whiteSpace: 'nowrap' }}>
                                        <button type="button" className="btn btn-sm btn-primary" title="Editar" onClick={() => openForm(config)}>
                                            <i className="bi bi-pencil" />
                                        </button>
                                        <button type="button" className="btn btn-sm btn-outline-secondary ms-1" title={config.activo ? 'Desactivar' : 'Activar'} onClick={() => toggleActive(config)}>
                                            <i className={`bi ${config.activo ? 'bi-pause-circle' : 'bi-play-circle'}`} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={6} className="text-center py-4 text-muted">
                                No hay configuraciones. Cree una configuración "Generica" para empezar a acumular.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {editing && (
                <FormModal
                    title={isNew ? 'Nueva configuración' : 'Editar configuración'}
                    subtitle={isNew ? undefined : `${editing.marca} · ${editing.moneda} · ${isStamps(editing) ? 'Sellos' : 'Puntos'}`}
                    onClose={() => setEditing(null)}
                    onSave={handleSave}
                    saving={saving}
                    error={formError}
                >
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label small text-muted mb-1" htmlFor="loyaltyConfigBrand">Marca *</label>
                            <select id="loyaltyConfigBrand" className={`form-select ${invalid('marca')}`} value={form.marca} disabled={!isNew}
                                onChange={(e) => setForm(prev => ({ ...prev, marca: e.target.value }))}>
                                {brandOptions.map(brand => <option key={brand} value={brand}>{brand}</option>)}
                            </select>
                            <div className="invalid-feedback">La marca es requerida.</div>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label small text-muted mb-1" htmlFor="loyaltyConfigCurrency">Moneda *</label>
                            <select id="loyaltyConfigCurrency" className="form-select" value={form.moneda} disabled={!isNew}
                                onChange={(e) => setForm(prev => ({ ...prev, moneda: e.target.value }))}>
                                <option value="CRC">CRC - Colones</option>
                                <option value="USD">USD - Dólares</option>
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label small text-muted mb-1" htmlFor="loyaltyConfigProgram">Programa *</label>
                            <select id="loyaltyConfigProgram" className="form-select" value={form.tipoPrograma} disabled={!isNew}
                                onChange={(e) => setForm(prev => ({ ...prev, tipoPrograma: e.target.value }))}>
                                <option value="puntos">Puntos</option>
                                <option value="sellos">Sellos</option>
                            </select>
                        </div>
                        {!isNew && (
                            <div className="col-12">
                                <div className="form-text mt-0">La marca, la moneda y el programa identifican la configuración y no se pueden cambiar.</div>
                            </div>
                        )}
                        {duplicate && (
                            <div className="col-12">
                                <div className="alert alert-warning py-2 small mb-0">
                                    Ya existe una configuración de {stamps ? 'sellos' : 'puntos'} para {form.marca.trim()} en {form.moneda}; al guardar se actualizará.
                                </div>
                            </div>
                        )}

                        {!stamps && (
                            <div className="col-md-6">
                                <label className="form-label small text-muted mb-1" htmlFor="loyaltyConfigAmount">Monto por punto *</label>
                                <div className="input-group has-validation">
                                    <span className="input-group-text">{currencySymbol(form.moneda)}</span>
                                    <input id="loyaltyConfigAmount" type="number" min={0} step="any" className={`form-control ${invalid('montoPorPunto')}`} value={form.montoPorPunto}
                                        onChange={(e) => setForm(prev => ({ ...prev, montoPorPunto: e.target.value }))} />
                                    <div className="invalid-feedback">Debe ser mayor a 0.</div>
                                </div>
                                {Number(form.montoPorPunto) > 0 && (
                                    <div className="form-text">Una compra de {currencySymbol(form.moneda)}{(Number(form.montoPorPunto) * 10).toLocaleString('en-US')} acumula 10 puntos.</div>
                                )}
                            </div>
                        )}
                        {stamps && (
                            <>
                                <div className="col-md-6">
                                    <label className="form-label small text-muted mb-1" htmlFor="loyaltyConfigStamps">Sellos por compra *</label>
                                    <input id="loyaltyConfigStamps" type="number" min={1} step={1} className={`form-control ${invalid('sellosPorCompra') || invalid('sellosPorCompraMeta')}`} value={form.sellosPorCompra}
                                        onChange={(e) => setForm(prev => ({ ...prev, sellosPorCompra: e.target.value }))} />
                                    <div className="invalid-feedback">
                                        {formErrors.sellosPorCompra ? 'Debe ser un número entero mayor o igual a 1.' : 'No puede ser mayor que los sellos para completar la tarjeta.'}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small text-muted mb-1" htmlFor="loyaltyConfigMeta">Sellos para completar la tarjeta *</label>
                                    <input id="loyaltyConfigMeta" type="number" min={1} max={STAMPS_META_MAX} step={1} className={`form-control ${invalid('sellosMeta')}`} value={form.sellosMeta}
                                        onChange={(e) => setForm(prev => ({ ...prev, sellosMeta: e.target.value }))} />
                                    <div className="invalid-feedback">Debe ser un número entero de 1 a {STAMPS_META_MAX}.</div>
                                    <div className="form-text">Este valor se aplica a todas las tarjetas de sellos de tus clientes, incluidas las que ya tienen sellos.</div>
                                </div>
                            </>
                        )}
                        <div className="col-12">
                            <div className="form-check form-switch">
                                <input className="form-check-input" type="checkbox" role="switch" id="loyaltyConfigActive" checked={form.activo}
                                    onChange={(e) => setForm(prev => ({ ...prev, activo: e.target.checked }))} />
                                <label className="form-check-label" htmlFor="loyaltyConfigActive">Configuración activa</label>
                            </div>
                        </div>
                    </div>
                </FormModal>
            )}

            {error.length > 0 && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
            {success.length > 0 && (
                <Message type="success" title="Exitoso" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
        </>
    );
};
